import React, { useEffect, useMemo, useState } from 'react';
import {
  RallyHitQuality,
  RallyStatus,
  useClaimRallyPlayerMutation,
  useRallyStateQuery,
  useRegisterRallyHitMutation,
  useReleaseRallyPlayerMutation,
} from '../../graphql-operations';
import Crayfish from './Crayfish';
import RaceTrack from './RaceTrack';
import Results from './Results';
import TimingControl from './TimingControl';
import './rally.css';

type Session = { raceId: string; playerId: string; claimToken: string };
const SESSION_KEY = 'kraftlund-rally-session';

const readSession = (): Session | null => {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch {
    return null;
  }
};

function Countdown({ startsAt, serverTime }: { startsAt: string; serverTime: string }) {
  const offset = useMemo(() => new Date(serverTime).getTime() - Date.now(), [serverTime]);
  const [remaining, setRemaining] = useState(
    new Date(startsAt).getTime() - (Date.now() + offset)
  );
  useEffect(() => {
    const timer = window.setInterval(
      () => setRemaining(new Date(startsAt).getTime() - (Date.now() + offset)),
      60
    );
    return () => window.clearInterval(timer);
  }, [startsAt, offset]);
  const value =
    remaining <= 500 ? 'KÖR!' : String(Math.max(1, Math.ceil((remaining - 500) / 1000)));
  return <div className="rally-countdown">{value}</div>;
}

export default function RallyPage() {
  const [session, setSession] = useState<Session | null>(readSession);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [ownProgress, setOwnProgress] = useState<number>();
  const { data, loading, refetch } = useRallyStateQuery({
    variables: { claimToken: session?.claimToken },
    pollInterval: 900,
    fetchPolicy: 'network-only',
  });
  const race = data?.rallyState;
  const [claimPlayer] = useClaimRallyPlayerMutation();
  const [releasePlayer] = useReleaseRallyPlayerMutation();
  const [registerHit] = useRegisterRallyHitMutation();

  useEffect(() => {
    if (session && race && session.raceId !== race.id) {
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      setOwnProgress(undefined);
    }
  }, [race, session]);

  const ownEntry = race?.entries.find((entry) => entry.id === session?.playerId);
  const ownFinished = Boolean(
    ownEntry && ownEntry.progress >= 100 && ownEntry.finishedAt
  );
  useEffect(() => {
    if (ownEntry && (ownProgress === undefined || ownEntry.progress > ownProgress)) {
      setOwnProgress(ownEntry.progress);
    }
  }, [ownEntry, ownProgress]);

  const selectPlayer = async (participantKey: string) => {
    if (!race) return;
    setClaiming(participantKey);
    setClaimError('');
    try {
      const result = await claimPlayer({
        variables: {
          participantKey,
          previousToken: session?.raceId === race.id ? session.claimToken : undefined,
        },
      });
      const claim = result.data?.claimRallyPlayer;
      if (claim) {
        const next = { raceId: race.id, ...claim };
        localStorage.setItem(SESSION_KEY, JSON.stringify(next));
        setSession(next);
        await refetch({ claimToken: claim.claimToken });
      }
    } catch (error) {
      setClaimError(
        error instanceof Error ? error.message : 'Det gick inte att välja deltagare.'
      );
    } finally {
      setClaiming(null);
    }
  };

  const changePlayer = async () => {
    if (!session || race?.status !== RallyStatus.Lobby) return;
    setReleasing(true);
    setClaimError('');
    try {
      const result = await releasePlayer({
        variables: { claimToken: session.claimToken },
      });
      if (!result.data?.releaseRallyPlayer) {
        throw new Error('Det gick inte att frigöra deltagaren.');
      }
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      setOwnProgress(undefined);
      // The mutation has already released the authoritative claim. A failed
      // refresh must not make the user think the release itself failed.
      await refetch({ claimToken: undefined }).catch(() => undefined);
    } catch (error) {
      setClaimError(
        error instanceof Error ? error.message : 'Det gick inte att byta deltagare.'
      );
    } finally {
      setReleasing(false);
    }
  };

  const hit = (quality: RallyHitQuality) => {
    if (!session || !ownEntry) return;
    registerHit({ variables: { claimToken: session.claimToken, quality } })
      .then((result) => {
        const serverProgress = result.data?.registerRallyHit.progress;
        if (serverProgress !== undefined) {
          setOwnProgress((current) => Math.max(current || 0, serverProgress));
        }
      })
      .catch(() => refetch());
  };

  if (loading && !race) {
    return <div className="rally-loading">🦞</div>;
  }
  if (!race) {
    return (
      <main className="rally-shell rally-empty">
        <Crayfish color="#e8442e" />
        <h1>Kräft-rally</h1>
        <p>Värdens startplatta är inte klar än.</p>
      </main>
    );
  }
  if (race.status === RallyStatus.Finished) {
    return <Results entries={race.entries} />;
  }

  const joined = race.entries.filter((entry) => entry.joined);
  const isRaceView = race.status !== RallyStatus.Lobby;

  return (
    <main className={`rally-shell${isRaceView ? ' rally-shell--race' : ''}`}>
      {!isRaceView && (
        <>
          <header className="rally-hero">
            <div className="rally-logo-crayfish">
              <Crayfish color="#ff5a3d" />
            </div>
            <p className="rally-eyebrow">KRÄFTSKIVANS SNABBASTE</p>
            <h1>Kräft-rally</h1>
            <p>Vem är du? Tryck på ditt namn.</p>
          </header>
          <section className="rally-player-list">
            {race.entries.map((entry) => {
              const selected = entry.id === session?.playerId;
              return (
                <button
                  type="button"
                  key={entry.id}
                  disabled={
                    (entry.claimed && !selected) ||
                    Boolean(session && !selected) ||
                    Boolean(claiming)
                  }
                  className={selected ? 'selected' : ''}
                  onClick={() => selectPlayer(entry.participantKey)}
                >
                  <span
                    className="rally-team-dot"
                    style={{ background: entry.teamColor }}
                  />
                  <span>
                    <strong>{entry.name}</strong>
                    <small>{entry.team}</small>
                  </span>
                  <b>{selected ? 'DU' : entry.claimed ? 'UPPTAGEN' : '›'}</b>
                </button>
              );
            })}
          </section>
          {claimError && <p className="rally-error">{claimError}</p>}
          {ownEntry && (
            <div className="rally-waiting">
              <span className="rally-pulse" />
              <strong>{ownEntry.name}, du är med!</strong>
              <small>Väntar på att värden startar · {joined.length} anslutna</small>
              <button type="button" onClick={changePlayer} disabled={releasing}>
                Fel person? <b>{releasing ? 'Byter namn…' : 'Byt namn'}</b>
              </button>
            </div>
          )}
        </>
      )}

      {isRaceView && (
        <div className="rally-game">
          <header className="rally-game-header">
            <div>
              <b>KRÄFT-RALLY</b>
              <small>{joined.length} kräftor på banan</small>
            </div>
            {ownEntry && (
              <span style={{ borderColor: ownEntry.teamColor }}>{ownEntry.name}</span>
            )}
          </header>
          <RaceTrack
            entries={joined}
            playerId={session?.playerId}
            ownProgress={ownProgress}
          />
          {race.status === RallyStatus.Countdown && race.startsAt && (
            <Countdown startsAt={race.startsAt} serverTime={race.serverTime} />
          )}
          {race.status === RallyStatus.Racing && ownEntry && !ownFinished && (
            <TimingControl onHit={hit} disabled={(ownProgress || 0) >= 100} />
          )}
          {race.status === RallyStatus.Racing && !ownEntry && (
            <div className="rally-spectating">Du tittar på loppet</div>
          )}
          {ownFinished && <div className="rally-finished-banner">I MÅL! 🎉</div>}
        </div>
      )}
    </main>
  );
}
